// Primitivas de la oficina (S10; rebrand Apple en modo noche 03-11-2026, docs/design-system.md §Oficina). Ninguna
// pantalla pinta un botón, un campo o una ventana a mano: todo sale de aquí, con la paleta `oficina.*` y los tokens
// `o-*` (letra, esquinas, sombras). Botones en cápsula, ventanas como hojas. Accesibles por construcción (Radix): foco
// visible, teclado, nombre para lectores.
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Check, ChevronDown, Loader2, X, type LucideIcon, Briefcase } from 'lucide-react';
import { forwardRef, useEffect, useMemo, useRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { persona, retratoDe } from '../../../lib/oficina/pixel-art';
import type { EstadoVisual } from '../compartido/vocabulario';

// Anillo blanco separado del borde: se ve igual sobre el grafito que sobre un botón de oro.
const FOCO = 'outline-none focus-visible:ring-2 focus-visible:ring-white/85 focus-visible:ring-offset-2 focus-visible:ring-offset-oficina-fondo';

// ── Widget (rebrand 03-11): las pantallas de la pared (ventas, cerebros, cuentas, Santi) en la fila de arriba de la
// oficina, como los widgets de Apple: todas del mismo alto, gris de ventana, esquinas de 22 px. Se pulsan enteras.
export const WIDGET =
  'group flex h-full min-h-[112px] w-full flex-col rounded-o-xl bg-oficina-elevada px-4 py-3 text-left shadow-o-flota outline-none transition-colors duration-150 ease-o-apple hover:bg-oficina-control focus-visible:ring-2 focus-visible:ring-oficina-enlace/70';

// ── Botón ────────────────────────────────────────────────────────────────

type Variante = 'primario' | 'secundario' | 'fantasma' | 'peligro';
const VARIANTE: Record<Variante, string> = {
  // Desactivado: gris neutro, nunca un azul lavado que parezca pulsable (crítica r2).
  primario: 'bg-oficina-acento text-oficina-acento-tinta hover:bg-oficina-acento-hover disabled:bg-oficina-control disabled:text-oficina-texto3 disabled:opacity-100',
  // Gris de relleno de Apple (sin borde): el botón que acompaña al principal.
  secundario: 'bg-oficina-control text-oficina-texto hover:bg-oficina-pulsado',
  fantasma: 'text-oficina-enlace hover:bg-oficina-control',
  peligro: 'bg-oficina-error text-white hover:bg-oficina-error/90',
};

export interface BotonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: Variante;
  tamano?: 'sm' | 'md';
  icono?: LucideIcon;
  cargando?: boolean;
}

export const Boton = forwardRef<HTMLButtonElement, BotonProps>(function Boton(
  { variante = 'secundario', tamano = 'md', icono: Icono, cargando, className, children, disabled, type = 'button', ...resto },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || cargando}
      className={cn(
        'inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full font-semibold transition-colors duration-150 ease-o-apple active:opacity-80 disabled:pointer-events-none disabled:opacity-45',
        tamano === 'sm' ? 'h-8 px-3.5 text-o-sub' : 'h-10 px-5 text-o-cuerpo',
        VARIANTE[variante],
        FOCO,
        className,
      )}
      {...resto}
    >
      {cargando ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : Icono ? <Icono className="h-4 w-4" aria-hidden /> : null}
      {children}
    </button>
  );
});

// ── Botón de icono con su nombre en un tooltip ───────────────────────────

export const TACTIL_44 = '[@media(pointer:coarse)]:h-touch-target [@media(pointer:coarse)]:w-touch-target';

export const BotonIcono = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { icono: LucideIcon; etiqueta: string; activo?: boolean; tamano?: 'sm' | 'md' }>(
  function BotonIcono({ icono: Icono, etiqueta, activo, tamano = 'md', className, type = 'button', ...resto }, ref) {
    return (
      <Pista texto={etiqueta}>
        <button
          ref={ref}
          type={type}
          aria-label={etiqueta}
          className={cn(
            'inline-flex shrink-0 items-center justify-center rounded-full text-oficina-texto2 transition-colors hover:bg-oficina-hover hover:text-oficina-texto disabled:pointer-events-none disabled:opacity-40',
            tamano === 'sm' ? 'h-8 w-8' : 'h-10 w-10',
            // 09-10 — con el dedo (móvil, iPad) el círculo crece a 44×44, el mínimo de Apple para acertar; con ratón,
            // igual que siempre.
            TACTIL_44,
            activo && 'bg-oficina-hover text-oficina-enlace',
            FOCO,
            className,
          )}
          {...resto}
        >
          <Icono className={tamano === 'sm' ? 'h-[18px] w-[18px]' : 'h-5 w-5'} aria-hidden />
        </button>
      </Pista>
    );
  },
);

export function Pista({ texto, children, lado = 'bottom' }: { texto: string; children: ReactNode; lado?: 'top' | 'bottom' | 'left' | 'right' }) {
  return (
    <TooltipPrimitive.Root delayDuration={350}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <ContenidoPista texto={texto} lado={lado} />
    </TooltipPrimitive.Root>
  );
}

function ContenidoPista({ texto, lado }: { texto: string; lado: 'top' | 'bottom' | 'left' | 'right' }) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        side={lado}
        sideOffset={6}
        className="z-[80] rounded-o-s bg-oficina-pulsado px-2.5 py-1 text-o-nota text-oficina-texto shadow-o-flota animate-in fade-in-0 zoom-in-95"
      >
        {texto}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

/** Una ayuda «i» (22-10, revisión de la sala): como Pista, pero además se abre y se cierra con un toque o un clic (con
 *  el dedo no hay «pasar por encima» y en el móvil no se abría ninguna). Solo para botones cuya única función es
 *  enseñar la ayuda: los botones de icono que HACEN algo (adjuntar, micrófono…) siguen con Pista, que no se queda
 *  pegada al pulsarlos. Tocar fuera la cierra (lo hace la capa de Radix). */
export function PistaAyuda({ texto, children, lado = 'bottom' }: { texto: string; children: ReactNode; lado?: 'top' | 'bottom' | 'left' | 'right' }) {
  const [abierta, setAbierta] = useState(false);
  // Radix cierra la pista al pulsar su disparador (pointerdown) antes del clic: se apunta cómo estaba ANTES de pulsar
  // para que el clic la alterne de verdad (si no, un segundo toque la cerraba y la volvía a abrir al momento).
  const antes = useRef(false);
  return (
    <TooltipPrimitive.Root delayDuration={350} open={abierta} onOpenChange={setAbierta}>
      <TooltipPrimitive.Trigger
        asChild
        onPointerDown={() => {
          antes.current = abierta;
        }}
        onClick={() => setAbierta(!antes.current)}
      >
        {children}
      </TooltipPrimitive.Trigger>
      <ContenidoPista texto={texto} lado={lado} />
    </TooltipPrimitive.Root>
  );
}

export const ProveedorPistas = TooltipPrimitive.Provider;

// ── Avatar: el retrato de la MISMA persona que se sienta en la oficina (crítica A4) ──
// Se dibuja con el mismo sprite del mundo (cabeza y hombros) sobre un fondo del color de su camiseta: así
// «Nora» en la lista es la chica del tercer escritorio. Las iniciales quedan solo para lectores de pantalla.

export const COLOR_PUNTO: Record<EstadoVisual, string> = {
  llegando: 'bg-oficina-esperando',
  saliendo: 'bg-oficina-esperando',
  error: 'bg-oficina-error',
  te_necesita: 'bg-oficina-te-necesita',
  bloqueado: 'bg-oficina-bloqueado',
  cerrando: 'bg-oficina-cerrando',
  trabajando: 'bg-oficina-trabajando',
  esperando: 'bg-oficina-esperando',
};

export function Avatar({ nombre, semilla, tamano = 42, estado, anillo }: { nombre: string; semilla: string; tamano?: number; estado?: EstadoVisual; anillo?: string }) {
  const lienzo = useRef<HTMLCanvasElement>(null);
  const retrato = useMemo(() => retratoDe(semilla), [semilla]);
  useEffect(() => {
    const ctx = lienzo.current?.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, 28, 28);
    // Sprite a escala 1 en un lienzo de 28×28 (se amplía con píxeles nítidos): cabeza arriba, hombros abajo.
    persona(ctx, 14, 5, retrato, 'lee', 0, false);
  }, [retrato]);
  const punto = Math.max(10, Math.round(tamano * 0.28));
  return (
    <span className="relative inline-flex shrink-0" style={{ width: tamano, height: tamano }} aria-hidden>
      <span className="flex h-full w-full items-end justify-center overflow-hidden rounded-full" style={{ background: `color-mix(in srgb, ${retrato.camisa} 34%, #121214)` }}>
        <canvas ref={lienzo} width={28} height={28} className="h-full w-full [image-rendering:pixelated]" />
        <span className="sr-only">{iniciales(nombre)}</span>
      </span>
      {estado ? (
        <span className={cn('absolute bottom-0 right-0 rounded-full', COLOR_PUNTO[estado], anillo ?? 'ring-2 ring-oficina-panel')} style={{ width: punto, height: punto }} />
      ) : null}
    </span>
  );
}

/**
 * 30-09 — el icono del ENCARGO (Pau: «los iconos de las personas no me sirven de nada, preferiría un logo que tenga
 * que ver con lo que se está trabajando»): el emoji que eligió la recepción a partir del título, sobre un color
 * suave propio de cada agente. Mientras no lo tiene, un maletín neutro.
 */
export function IconoEncargo({ icono, semilla, tamano = 42 }: { icono: string | null; semilla: string; tamano?: number }) {
  let h = 0;
  for (const ch of semilla) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-o-m"
      style={{ width: tamano, height: tamano, background: `hsl(${h} 32% 26%)` }}
      aria-hidden
    >
      {icono ? (
        <span style={{ fontSize: Math.round(tamano * 0.52), lineHeight: 1 }}>{icono}</span>
      ) : (
        <Briefcase className="text-oficina-texto2" style={{ width: Math.round(tamano * 0.46), height: Math.round(tamano * 0.46) }} />
      )}
    </span>
  );
}

export function iniciales(nombre: string): string {
  const partes = nombre.trim().normalize('NFD').replace(/\p{M}/gu, '').split(/\s+/).filter(Boolean);
  const letras = partes.length > 1 ? `${partes[0]![0]}${partes[1]![0]}` : (partes[0] ?? '?').slice(0, 2);
  return letras.toUpperCase();
}

// ── Insignia (contador o palabra corta) ──────────────────────────────────

export function Insignia({ children, tono = 'acento' }: { children: ReactNode; tono?: 'acento' | 'apagado' | 'aviso' | 'error' }) {
  const color = {
    acento: 'bg-oficina-te-necesita text-oficina-acento-tinta',
    apagado: 'bg-oficina-linea text-oficina-texto2',
    aviso: 'bg-oficina-bloqueado text-oficina-fondo',
    error: 'bg-oficina-error text-white',
  }[tono];
  return <span className={cn('inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-o-nota font-semibold leading-none', color)}>{children}</span>;
}

// ── Campos ───────────────────────────────────────────────────────────────

const CAMPO =
  // Campo de Apple: relleno gris sin borde visible; al escribir, anillo de oro.
  'w-full rounded-o-m border border-transparent bg-oficina-control px-3.5 text-o-cuerpo text-oficina-texto placeholder:text-oficina-texto3 transition-colors duration-150 hover:bg-oficina-pulsado/70 focus:border-oficina-acento/70 focus:bg-oficina-control focus:ring-2 focus:ring-oficina-acento/25 disabled:opacity-45 outline-none';

export const Campo = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Campo({ className, ...resto }, ref) {
  return <input ref={ref} className={cn(CAMPO, 'h-10', className)} {...resto} />;
});

export const AreaTexto = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function AreaTexto({ className, ...resto }, ref) {
  return <textarea ref={ref} className={cn(CAMPO, 'resize-none py-2.5 leading-relaxed', className)} {...resto} />;
});

export function Etiqueta({ titulo, ayuda, children }: { titulo: string; ayuda?: string; children: ReactNode }) {
  return (
    <label className="grid gap-1.5">
      <span className="flex items-center gap-2 text-o-texto font-medium text-oficina-texto">
        {titulo}
      </span>
      {children}
      {ayuda ? <span className="text-o-sub text-oficina-texto3">{ayuda}</span> : null}
    </label>
  );
}

/**
 * Opciones como pastillas (una elegida). Para pocas opciones visibles de un vistazo.
 * `llenar` (10-10, punto 9 del informe de espacio): una sola fila que reparte el ancho, como pestañas; en el móvil
 * «Resumen / Más información / Hablar» saltaban a 2 filas.
 */
export function Pastillas<T extends string>({ opciones, valor, alCambiar, etiqueta, llenar = false }: { opciones: ReadonlyArray<{ valor: T; texto: string; pista?: string }>; valor: T | null; alCambiar: (v: T) => void; etiqueta: string; llenar?: boolean }) {
  return (
    <div role="radiogroup" aria-label={etiqueta} className={cn('flex gap-1.5', llenar ? 'flex-nowrap' : 'flex-wrap')}>
      {opciones.map((o) => {
        const activa = o.valor === valor;
        return (
          <button
            key={o.valor}
            type="button"
            role="radio"
            aria-checked={activa}
            title={o.pista}
            onClick={() => alCambiar(o.valor)}
            className={cn(
              'h-8 rounded-full px-3.5 text-o-sub font-medium transition-colors duration-150',
              llenar && 'min-w-0 flex-auto truncate px-2 sm:flex-none sm:px-3.5',
              activa ? 'bg-oficina-texto font-semibold text-oficina-fondo' : 'bg-oficina-control text-oficina-texto2 hover:bg-oficina-pulsado hover:text-oficina-texto',
              FOCO,
            )}
          >
            {o.texto}
          </button>
        );
      })}
    </div>
  );
}

// ── Ventana (diálogo modal) ──────────────────────────────────────────────

export function Ventana({
  abierta,
  alCerrar,
  titulo,
  descripcion,
  children,
  pie,
  ancho = 'md',
  completaEnMovil = false,
}: {
  abierta: boolean;
  alCerrar: () => void;
  titulo: ReactNode;
  descripcion?: ReactNode;
  children: ReactNode;
  pie?: ReactNode;
  ancho?: 'sm' | 'md' | 'lg';
  /** En el móvil ocupa la pantalla entera, como una hoja de una app (la tarjeta del Allow, 13-10); desde sm, flotante. */
  completaEnMovil?: boolean;
}) {
  return (
    <DialogPrimitive.Root open={abierta} onOpenChange={(o) => !o && alCerrar()}>
      <DialogPrimitive.Portal>
        {/* Sin desenfoque: sobre el lienzo animado de la oficina parpadea en Chrome/Windows (28-09, marco/estado.ts). */}
        <DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-black/55 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          // Crítica M3: al abrir no se pinta el foco en la ✕. Va al elemento marcado con data-autofoco (p. ej.
          // el texto del encargo) o a la propia ventana (sigue siendo accesible con teclado).
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            const raiz = e.currentTarget as HTMLElement;
            (raiz.querySelector<HTMLElement>('[data-autofoco]') ?? raiz).focus({ preventScroll: true });
          }}
          tabIndex={-1}
          className={cn(
            'fixed z-[61] flex flex-col overflow-hidden bg-oficina-elevada text-oficina-texto shadow-o-hoja outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-300 data-[state=open]:ease-o-apple',
            completaEnMovil
              ? 'inset-0 h-[100dvh] pt-[env(safe-area-inset-top)] sm:inset-auto sm:left-1/2 sm:top-1/2 sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:w-[calc(100vw-2rem)] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-o-l sm:pt-0 sm:data-[state=open]:zoom-in-95'
              : 'left-1/2 top-1/2 max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-o-l data-[state=open]:zoom-in-95',
            { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl' }[ancho],
          )}
        >
          {/* 10-10 (puntos 9 y 10): hasta 1 rem del borde de la pantalla antes de desplazarse (antes 48 px arriba y
              abajo: en el portátil el pie tapaba «Si sale mal»); en el móvil, 16 px a los lados en vez de 24. */}
          <header className="flex items-start gap-3 px-4 pb-2 pt-5 sm:px-6 sm:pt-6">
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="text-o-titulo2 font-semibold">{titulo}</DialogPrimitive.Title>
              {descripcion ? (
                <DialogPrimitive.Description className="mt-1 text-o-texto text-oficina-texto2">{descripcion}</DialogPrimitive.Description>
              ) : (
                <DialogPrimitive.Description className="sr-only">{typeof titulo === 'string' ? titulo : ''}</DialogPrimitive.Description>
              )}
            </div>
            {/* Sin pista: al abrir, el foco cae aquí y una pista encendida sola despista; la ✕ se entiende. */}
            <DialogPrimitive.Close
              aria-label={t('oficinaV2.chat.cerrarVentana')}
              // La ✕ de las hojas de Apple: círculo gris relleno.
              className={cn('-mr-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-oficina-control text-oficina-texto2 transition-colors hover:bg-oficina-pulsado hover:text-oficina-texto', FOCO)}
            >
              <X className="h-4 w-4" strokeWidth={2.5} aria-hidden />
            </DialogPrimitive.Close>
          </header>
          <CuerpoDesplazable>{children}</CuerpoDesplazable>
          {pie ? <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-oficina-linea px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4 sm:px-6 sm:pb-5">{pie}</footer> : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/**
 * Cuerpo de ventana que avisa con un degradado cuando hay más contenido debajo (crítica M1). 10-10 (punto 9): el de
 * 32 px no se veía; ahora más alto, más opaco y con una sombra contra el pie. `scroll-pb-16`: lo que se enfoca o se
 * desplaza a la vista (el último campo) queda por encima del degradado, no debajo.
 */
function CuerpoDesplazable({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [mas, setMas] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const medir = () => setMas(el.scrollHeight - el.scrollTop - el.clientHeight > 4);
    medir();
    el.addEventListener('scroll', medir, { passive: true });
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', medir);
      ro.disconnect();
    };
  }, []);
  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div ref={ref} data-testid="cuerpo-ventana" className="min-h-0 flex-1 scroll-pb-16 overflow-y-auto px-4 pb-4 pt-3 [scrollbar-width:thin] sm:px-6">
        {children}
      </div>
      {mas ? (
        <div
          data-testid="mas-abajo"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-oficina-elevada via-oficina-elevada/75 to-transparent shadow-[inset_0_-10px_12px_-10px] shadow-black/70"
          aria-hidden
        />
      ) : null}
    </div>
  );
}

/** Casilla propia (18 px, radio 4, acento al marcar): la nativa del navegador rompía el sistema (crítica M3). */
export function Casilla({ marcada, alCambiar, children }: { marcada: boolean; alCambiar: (v: boolean) => void; children: ReactNode }) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 text-o-texto text-oficina-texto2">
      <input type="checkbox" checked={marcada} onChange={(e) => alCambiar(e.target.checked)} className="peer sr-only" />
      <span
        className={cn(
          'mt-[1px] flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-oficina-enlace/70',
          marcada ? 'border-oficina-acento bg-oficina-acento text-oficina-acento-tinta' : 'border-oficina-texto3 bg-transparent',
        )}
        aria-hidden
      >
        {marcada ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : null}
      </span>
      <span>{children}</span>
    </label>
  );
}

/** Aviso en línea (error, advertencia o información) dentro de un formulario o tarjeta. */
export function Aviso({ tono = 'info', children }: { tono?: 'info' | 'aviso' | 'error'; children: ReactNode }) {
  const c = {
    info: 'border-oficina-acento/25 bg-oficina-acento/10 text-oficina-texto',
    aviso: 'border-oficina-bloqueado/25 bg-oficina-bloqueado/10 text-oficina-texto',
    error: 'border-oficina-error/30 bg-oficina-error/10 text-oficina-texto',
  }[tono];
  return (
    <p role={tono === 'error' ? 'alert' : undefined} className={cn('rounded-o-m border px-3.5 py-2.5 text-o-texto', c)}>
      {children}
    </p>
  );
}

/** Desplegable nativo (accesible y con teclado de serie) con la paleta de la oficina. */
export const Selector = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Selector({ className, children, ...resto }, ref) {
  return (
    <span className="relative block">
      <select ref={ref} className={cn(CAMPO, 'h-10 cursor-pointer appearance-none pr-9 [color-scheme:dark]', className)} {...resto}>
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-oficina-texto3" aria-hidden />
    </span>
  );
});

/**
 * Control segmentado de Apple (03-11-2026): pocas opciones excluyentes en una cápsula gris; la elegida, en relieve.
 * Para cambiar de vista o de rango (Por estado / Por sala, Hoy / 7 D…). Teclado: flechas entre opciones.
 */
export function Segmentado<T extends string>({ opciones, valor, alCambiar, etiqueta, className }: { opciones: ReadonlyArray<{ valor: T; texto: string }>; valor: T; alCambiar: (v: T) => void; etiqueta: string; className?: string }) {
  const mover = (dir: 1 | -1) => {
    const i = opciones.findIndex((o) => o.valor === valor);
    const sig = opciones[(i + dir + opciones.length) % opciones.length];
    if (sig) alCambiar(sig.valor);
  };
  return (
    <div
      role="radiogroup"
      aria-label={etiqueta}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); mover(1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); mover(-1); }
      }}
      className={cn('inline-flex shrink-0 rounded-o-s bg-oficina-control p-0.5', className)}
    >
      {opciones.map((o) => {
        const activa = o.valor === valor;
        return (
          <button
            key={o.valor}
            type="button"
            role="radio"
            aria-checked={activa}
            tabIndex={activa ? 0 : -1}
            onClick={() => alCambiar(o.valor)}
            className={cn(
              'h-7 min-h-0 min-w-0 whitespace-nowrap rounded-o-s px-3 text-o-sub font-medium transition-colors duration-150 ease-o-apple',
              activa ? 'bg-oficina-pulsado text-oficina-texto shadow-o-flota' : 'text-oficina-texto2 hover:text-oficina-texto',
              FOCO,
            )}
          >
            {o.texto}
          </button>
        );
      })}
    </div>
  );
}
